import React from 'react';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { formatDateLong } from './formatters';
import type { AntibioticImpact, AntibioticWithdrawalDetail } from './types';
import css from './DigitalTwinView.module.css';

interface AntibioticImpactBannerProps {
  antibioticImpact: AntibioticImpact;
}

const daysFromToday = (dateStr: string | null | undefined): number => {
  if (!dateStr) {
    return 0;
  }

  const target = new Date(dateStr);
  const today = new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
};

const resolveWaitingDays = (antibioticImpact: AntibioticImpact): number => {
  if ((antibioticImpact.requiredWithdrawalDays ?? 0) > 0) {
    return antibioticImpact.requiredWithdrawalDays ?? 0;
  }

  const fromTreatments = antibioticImpact.activeWithdrawals.map(
    (withdrawal: AntibioticWithdrawalDetail) => withdrawal.waitingTimeDays
  );
  if (fromTreatments.length > 0) {
    return Math.max(0, ...fromTreatments);
  }

  const clearanceDate = antibioticImpact.latestClearanceDate;
  if (!clearanceDate) {
    return 0;
  }

  const longestSpan = antibioticImpact.activeWithdrawals.reduce((maxSpan, withdrawal) => {
    const injectionDate = new Date(withdrawal.injectionDate);
    const clearance = new Date(withdrawal.clearanceDate);
    injectionDate.setHours(0, 0, 0, 0);
    clearance.setHours(0, 0, 0, 0);
    const span = Math.round((clearance.getTime() - injectionDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(maxSpan, span);
  }, 0);

  return longestSpan;
};

const resolveDaysUntilClearance = (antibioticImpact: AntibioticImpact): number => {
  if ((antibioticImpact.daysUntilClearance ?? 0) > 0) {
    return antibioticImpact.daysUntilClearance ?? 0;
  }

  return daysFromToday(antibioticImpact.latestClearanceDate);
};

export const AntibioticImpactBanner: React.FC<AntibioticImpactBannerProps> = ({ antibioticImpact }) => {
  const { t } = useTranslation();

  const activeCount = antibioticImpact.activeWithdrawals.length;
  const waitingDays = resolveWaitingDays(antibioticImpact);
  const daysUntilClearance = resolveDaysUntilClearance(antibioticImpact);

  if (!antibioticImpact.hasActiveWithdrawal && antibioticImpact.delayDays <= 0 && waitingDays <= 0) {
    return null;
  }

  const clearanceDate =
    antibioticImpact.latestClearanceDate != null ? formatDateLong(antibioticImpact.latestClearanceDate) : null;

  const message =
    antibioticImpact.delayDays > 0
      ? t('digitalTwin.antibiotic.warningDelayed', {
          delayDays: antibioticImpact.delayDays,
          waitingDays,
          count: activeCount,
          defaultValue:
            'Slaughter is delayed by {{delayDays}} days. Product withdrawal: {{waitingDays}} days ({{count}} active treatment(s)).'
        })
      : t('digitalTwin.antibiotic.warningIncludedInSchedule', {
          count: activeCount,
          waitingDays,
          daysUntilClearance,
          clearanceDate: clearanceDate ?? '—',
          defaultValue:
            '{{count}} active treatment(s). Withdrawal period: {{waitingDays}} days (included in slaughter date). Antibiotic-free from {{clearanceDate}} ({{daysUntilClearance}} days from today).'
        });

  return (
    <div className={css.withdrawalBanner}>
      <Typography className={css.withdrawalTitle} component="h4">
        {t('digitalTwin.antibiotic.warningTitle', {
          defaultValue: 'Antibiotic withdrawal period'
        })}
      </Typography>
      <Typography className={css.withdrawalText}>{message}</Typography>
    </div>
  );
};
